/**
 * Paneles de control de acceso de Holanda — inventario, configuración y puertas.
 *
 * Son los equipos que abren las puertas peatonales y las plumas vehiculares de
 * cada agencia. Hasta la migración 076 estaban dados de alta como biométricos,
 * y por eso no tenían pantalla: el detector de deriva de biométricos **no les
 * encuentra un solo campo** — un panel no tiene lector de huella ni de cara.
 *
 * Dos cosas que esta pantalla NO hace, y es a propósito:
 *
 * - **No alarma por el tipo de contacto magnético ni de botón.** Se ven, y se
 *   ve cuál se sale del reparto, pero un panel vehicular con el contacto al
 *   revés puede ser una pluma montada distinto y no un error. Hasta que
 *   alguien de sitio diga cuál es el correcto para cada tipo de acceso, es un
 *   dato, no un incumplimiento.
 * - **No muestra las claves de puerta.** El equipo las entrega en el mismo
 *   payload (coacción, superusuario, apertura) y se descartan al cosechar: lo
 *   que no se guarda no se puede filtrar después.
 *
 * Lo que sí va al frente es la **cobertura**. Un panel sin ruta a su agencia no
 * cumple ni incumple, y mezclarlo con los demás es el error de los 37 rojos
 * falsos del dashboard de Holanda.
 */
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Desvios,
  ESTABILIDAD,
  Flag,
  GoldenReparto,
  ResumenCobertura,
  Tarjeta,
} from "@/components/ConfigComunes";
import { usePaneles } from "@/hooks/useConfig";
import type { PanelFila, PanelPuerta } from "@/api/types";
import { cn } from "@/lib/utils";
import { Fragment, useState } from "react";

/** Los tiempos y los tipos de una puerta, en una línea legible. */
function Puerta({ p }: { p: PanelPuerta }) {
  const tiempos = [
    p.tiempo_apertura === null ? null : `abre ${p.tiempo_apertura}s`,
    p.tiempo_max_abierta === null ? null : `máx ${p.tiempo_max_abierta}s`,
    p.alarma_magnetico === null ? null : `alarma ${p.alarma_magnetico}s`,
  ].filter(Boolean);
  return (
    <div className="text-xs">
      <span className="text-muted-foreground">{p.numero}.</span>{" "}
      <span className="font-medium">{p.nombre ?? "(sin nombre)"}</span>
      {tiempos.length ? (
        <span className="text-muted-foreground"> · {tiempos.join(" · ")}</span>
      ) : null}
      {p.tipo_magnetico || p.tipo_boton ? (
        <span className="font-mono text-muted-foreground">
          {" "}
          · {p.tipo_magnetico ?? "—"}/{p.tipo_boton ?? "—"}
        </span>
      ) : null}
    </div>
  );
}

export function PanelesPanel() {
  const { data, isPending, error } = usePaneles();
  const [soloProblemas, setSoloProblemas] = useState(false);
  const [verGolden, setVerGolden] = useState(false);
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set());
  const alternar = (agencia: string) =>
    setAbiertas((previas) => {
      const s = new Set(previas);
      if (s.has(agencia)) s.delete(agencia);
      else s.add(agencia);
      return s;
    });

  if (isPending && !data)
    return (
      <div className="text-sm text-muted-foreground">Cargando paneles…</div>
    );
  if (error)
    return (
      <div className="text-sm text-destructive">
        No se pudieron cargar los paneles.
      </div>
    );
  if (!data) return null;
  if (data._status === "sin_configurar")
    return <div className="text-sm text-muted-foreground">{data.mensaje}</div>;

  const t = data.totales!;
  const filas = (data.paneles ?? []).filter((p: PanelFila) =>
    soloProblemas ? !p.leido || !p.cumple : true,
  );
  // Los paneles cuelgan de su agencia. El orden de las agencias lo decide el
  // backend (primero donde hay desvíos críticos), y dentro de cada una se
  // conserva el orden global.
  const porAgencia = new Map<string, PanelFila[]>();
  for (const p of filas) {
    const k = p.agencia ?? "(sin agencia)";
    porAgencia.set(k, [...(porAgencia.get(k) ?? []), p]);
  }
  const agencias = (data.agencias ?? []).filter((a) =>
    porAgencia.has(a.nombre),
  );

  return (
    <div className="space-y-4">
      <ResumenCobertura
        totales={{
          total: t.total ?? t.paneles,
          leidos: t.leidos,
          cobertura_pct: t.cobertura_pct,
          cumplen: t.cumplen,
          revirtieron: t.revirtieron,
          fuera: t.fuera,
          criticos: t.criticos,
          sin_leer: t.sin_leer,
          no_responden: t.no_responden ?? 0,
        }}
        extra={
          <Tarjeta titulo="Puertas" valor={t.puertas} nota="leídas en toda la flota" />
        }
      />

      <div className="flex items-center gap-2 text-sm">
        <button
          type="button"
          onClick={() => setSoloProblemas((v) => !v)}
          className={cn(
            "rounded border px-3 py-1",
            soloProblemas ? "bg-muted font-medium" : "text-muted-foreground",
          )}
        >
          Sólo con problema
        </button>
        <button
          type="button"
          onClick={() => setVerGolden((v) => !v)}
          className={cn(
            "rounded border px-3 py-1",
            verGolden ? "bg-muted font-medium" : "text-muted-foreground",
          )}
        >
          Ver el golden y su reparto
        </button>
        <span className="text-xs text-muted-foreground">
          {filas.length} de {t.paneles}
        </span>
      </div>

      {verGolden ? <GoldenReparto golden={data.golden ?? {}} /> : null}

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Agencia</TableHead>
              <TableHead className="text-right">Paneles</TableHead>
              <TableHead className="text-right">Cumplen</TableHead>
              <TableHead className="text-right">Fuera</TableHead>
              <TableHead className="text-right">Revirtieron</TableHead>
              <TableHead className="text-right">Sin leer</TableHead>
              <TableHead className="text-right">Puertas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {agencias.map((a) => {
              const suyos = porAgencia.get(a.nombre) ?? [];
              const abierta = abiertas.has(a.nombre);
              const revirtieron = suyos.filter(
                (p) => p.estabilidad.estado === "revirtio",
              ).length;
              return (
                <Fragment key={a.nombre}>
                  <TableRow
                    onClick={() => alternar(a.nombre)}
                    className="cursor-pointer"
                    aria-expanded={abierta}
                  >
                    <TableCell className="font-medium">
                      <span className="mr-1 inline-block w-3 text-muted-foreground">
                        {abierta ? "▾" : "▸"}
                      </span>
                      {a.nombre}
                    </TableCell>
                    <TableCell className="text-right">{a.paneles}</TableCell>
                    <TableCell className="text-right">{a.cumplen}</TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        a.criticos > 0 && "text-destructive",
                      )}
                    >
                      {a.fuera}
                      {a.criticos > 0 ? (
                        <span className="ml-1 text-xs">
                          ({a.criticos} crít.)
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        revirtieron > 0 && "text-destructive",
                      )}
                    >
                      {revirtieron || "—"}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {a.sin_leer || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      {a.puertas || "—"}
                    </TableCell>
                  </TableRow>
                  {abierta ? (
                    <TableRow>
                      <TableCell colSpan={7} className="bg-muted/40 p-0">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-muted-foreground">
                            <tr>
                              <th className="px-3 py-1 text-left">Panel</th>
                              <th className="px-3 py-1 text-left">IP</th>
                              <th className="px-3 py-1 text-left">
                                Estabilidad
                              </th>
                              <th className="px-3 py-1 text-left">Desvíos</th>
                              <th className="px-3 py-1 text-left">Puertas</th>
                              <th className="px-3 py-1 text-left">Modelo</th>
                              <th className="px-3 py-1 text-left">Firmware</th>
                              <th className="px-3 py-1 text-left">
                                Zona horaria
                              </th>
                              <th className="px-3 py-1 text-left">NTP</th>
                              <th className="px-3 py-1 text-center">DHCP</th>
                            </tr>
                          </thead>
                          <tbody>
                            {suyos.map((p) => {
                              const e =
                                ESTABILIDAD[p.estabilidad.estado] ??
                                ESTABILIDAD.sin_lectura;
                              return (
                                <tr key={p.device_id} className="border-t">
                                  <td
                                    className="px-3 py-2"
                                    title={p.nombre ?? ""}
                                  >
                                    {p.nombre ?? "—"}
                                    <Badge variant="outline" className="ml-2">
                                      {p.familia}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-xs">
                                    {p.ip}
                                  </td>
                                  <td className="px-3 py-2">
                                    <div title={e.ayuda}>
                                      <Badge variant={e.variant}>
                                        {e.texto}
                                      </Badge>
                                      {p.estabilidad.cambios > 0 ? (
                                        <div className="mt-1 text-xs text-muted-foreground">
                                          {p.estabilidad.cambios} cambio
                                          {p.estabilidad.cambios === 1
                                            ? ""
                                            : "s"}{" "}
                                          en {p.estabilidad.lecturas} lecturas
                                        </div>
                                      ) : null}
                                    </div>
                                  </td>
                                  {p.leido ? (
                                    <>
                                      <td className="min-w-[18rem] px-3 py-2">
                                        <Desvios filas={p.desvios} />
                                      </td>
                                      <td className="min-w-[16rem] px-3 py-2">
                                        {p.puertas_detalle.length ? (
                                          <div className="space-y-1">
                                            {p.puertas_detalle.map((d) => (
                                              <Puerta key={d.numero} p={d} />
                                            ))}
                                          </div>
                                        ) : (
                                          <span className="text-muted-foreground">
                                            —
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-xs">
                                        {p.modelo ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-xs">
                                        {p.firmware ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 font-mono text-xs">
                                        {p.zona_horaria ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 font-mono text-xs">
                                        {p.ntp ?? "—"}
                                      </td>
                                      <td className="px-3 py-2 text-center">
                                        <Flag valor={p.dhcp} malo />
                                      </td>
                                    </>
                                  ) : (
                                    <td
                                      colSpan={7}
                                      className="px-3 py-2 text-xs text-muted-foreground"
                                    >
                                      Sin leer — {p.motivo}
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <p className="text-xs text-muted-foreground">
        La <strong>cobertura</strong> va primero a propósito: un cumplimiento
        del 100 % sobre la mitad de la flota no es un 100 %. Y la{" "}
        <strong>estabilidad</strong> sale de comparar las fotos guardadas de
        cada equipo — hace falta más de una lectura para que signifique algo.
        <br />
        El tipo de contacto magnético y el de botón{" "}
        <strong>se muestran pero no alarman</strong>: hasta que alguien de sitio
        diga cuál corresponde a cada tipo de acceso, una diferencia es un dato y
        no un incumplimiento. Las claves de puerta que entrega el equipo no se
        guardan.
      </p>
    </div>
  );
}
